<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="onSave()"
      @close="onCancel()"
      size="md-lg"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
        >
          <h5 class="modal-title">Statement for {{ patientName ?? "" }}</h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="onCancel()"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div
          class="b-row d-flex justify-content-between align-items-center w-100"
        >
          <div class="b-col">
            <span class="lower-font-size text-danger text-sm-left">{{
              error
            }}</span>
          </div>
          <div class="b-col text-right mt-1 mr-2">
            <b-button @click="onCancel()" variant="secondary">Close</b-button>
            <b-button class="ml-2" variant="primary" @click="printSingle">
              Print
            </b-button>
          </div>
        </div>
      </template>

      <div
        id="printThis"
        class="card m-3 px-4 py-4 rounded-lg b-grid justify-content-between"
      >
        <b-row cols="2" align-v="center">
          <b-col>
            <p class="b-title h3 mb-0">Statement</p>
            <p class="b-title lower-font-size">
              For {{ formatDateOnlyPrettyShort(statement.dateFrom) }} -
              {{ formatDateOnlyPrettyShort(statement.dateTo) }}
            </p>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pb-2">
              Organization ({{ systSettingsData?.organizationName ?? "" }})
            </div>
            <div style="line-height:15px; text-align: right">
              <div>
                {{ systSettingsData?.organizationAddressLine1 ?? "" }}
                {{ systSettingsData?.organizationAddressLine2 ?? "" }}
              </div>
              <div>{{ systSettingsData?.organizationCity ?? "" }}</div>
              <div>{{ organizationState }}</div>
              <div>{{ systSettingsData?.organizationZipCode ?? "" }}</div>
            </div>
          </b-col>
        </b-row>
        <hr />
        <b-row class="d-flex justify-content-between align-content-center mb-4">
          <b-col
            class="d-flex justify-content-start flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">To</div>
            <div style="line-height:15px; text-align: left">
              <div
                v-if="
                  statement.billTo?.length > 0 &&
                    statement.billTo.includes('\n')
                "
              >
                <p
                  v-for="text in statement.billTo.split('\n') ?? []"
                  :key="text.index"
                  class="my-0"
                >
                  {{ text }}
                </p>
              </div>
              <div v-else>
                <p class="my-0">
                  {{ statement.billTo ?? "" }}
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">
              Statement #
            </div>
            <div># {{ statement.refNumber }}</div>
            <div class="font-weight-bold pt-2">Issued</div>
            <div>
              {{ statement.issuedDate }}
            </div>
          </b-col>
        </b-row>
        <b-row class="d-flex justify-content-between align-content-center">
          <b-col
            class="d-flex w-100 justify-content-center flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Client</div>
            <div
              class="w-100"
              v-if="
                statement.clientData?.length &&
                  statement.clientData.includes('\n')
              "
            >
              <p
                v-for="text in statement.clientData.split('\n')"
                :key="text.index"
                class="my-0"
              >
                {{ text }}
              </p>
            </div>
            <div v-else>
              {{ statement.clientData ?? "" }}
            </div>
          </b-col>
          <b-col
            class="d-flex w-100 justify-content-center flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Provider</div>
            <div
              class="w-100"
              v-if="
                statement.providerData?.length &&
                  statement.providerData.includes('\n')
              "
            >
              <p
                v-for="text in statement.providerData.split('\n')"
                :key="text.index"
                class="my-0"
              >
                {{ text }}
              </p>
            </div>
            <div v-else>
              {{ statement.providerData ?? "" }}
            </div>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0 border-0" />
        <p class="font-weight-bold my-0">Summary</p>
        <hr class="pb-2 mb-0 mt-1 border-secondary" />
        <b-row class="lower-font-size" align-v="center">
          <b-col cols="6">
            <p class="my-0">Beginning Balance</p>
            <p class="my-0">As of {{ formatDateOnly(statement.dateFrom) }}</p>
          </b-col>
          <b-col cols="6">
            <p class="my-0 text-right">
              {{ toCurrencyValue(statement.beginingBalance) }}
            </p>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0 mt-1" />
        <b-row class="lower-font-size" align-v="center">
          <b-col cols="6">
            <p class="my-0">Invoices</p>
          </b-col>
          <b-col cols="6">
            <p class="my-0 text-right">
              {{ toCurrencyValue(statement.invoicesTotal) }}
            </p>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0 mt-1" />
        <b-row class="lower-font-size" align-v="center">
          <b-col cols="6">
            <p class="my-0">Payments</p>
          </b-col>
          <b-col cols="6">
            <p class="my-0 text-right">
              {{ "- " + toCurrencyValue(statement.paymentsTotal) }}
            </p>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0 mt-1" />
        <b-row class="lower-font-size" align-v="center">
          <b-col cols="6">
            <p class="my-0">Ending Balance</p>
            <p class="my-0">As of {{ formatDateOnly(statement.dateTo) }}</p>
          </b-col>
          <b-col cols="6">
            <p class="my-0 text-right font-weight-bold">
              {{ toCurrencyValue(statement.endingBalance) }}
            </p>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0 mt-1" />
        <hr class="pb-2 mb-0 border-0" />
        <p class="font-weight-bold my-0">Details</p>
        <hr class="pb-2 mb-0 mt-1 border-secondary" />
        <div class=" w-100">
          <table class="w-100 text-black-50 lower-font-size">
            <thead>
              <tr>
                <th class="font-weight-bold w-25">Date</th>
                <th class="font-weight-bold w-50">Description</th>
                <th class="font-weight-bold text-right">Charges</th>
                <th class="font-weight-bold text-right">Payments</th>
                <th class="font-weight-bold text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="pb-2 mb-1 mt-1 border-bottom"
                v-for="(item, index) in statement.lineItems"
                :key="item.id"
              >
                <td class="py-2">{{ formatDateOnly(item.date) }}</td>
                <td
                  class="py-2"
                  v-if="
                    item.description?.length && item.description.includes('\n')
                  "
                >
                  <p
                    class="my-0"
                    v-for="line in item.description.split('\n')"
                    :key="line.index"
                  >
                    {{ line }}
                  </p>
                </td>
                <td class="py-2" v-else>
                  {{ item.description ?? "" }}
                </td>
                <td class="text-right py-2">
                  {{ item.type == 0 ? toCurrencyValue(item.amount) : "- -" }}
                </td>
                <td class="text-right py-2">
                  {{
                    item.type == 1
                      ? item.description.split(" ")[0] == "Refund"
                        ? "- " + toCurrencyValue(item.amount)
                        : toCurrencyValue(item.amount)
                      : "- -"
                  }}
                </td>
                <td class="text-right py-2">
                  {{
                    index == 0
                      ? calculateLineItemsBalance(item.amount, false, true)
                      : item.description.split(" ")[0] == "Payment"
                      ? calculateLineItemsBalance(item.amount, true)
                      : calculateLineItemsBalance(item.amount, false)
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr class="pb-2 mb-0 border-0" />
        <p class="font-weight-bold my-0">Uninvoiced Appointments</p>
        <hr class="pb-2 mb-0 mt-1 border-secondary" />
        <div class=" w-100">
          <table class="w-100 text-black-50 lower-font-size">
            <thead>
              <tr>
                <th class="font-weight-bold w-75">Description</th>
                <th class="font-weight-bold w-25 text-right">Fee</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="pb-2 mb-1 mt-1 border-bottom"
                v-for="item in statement.withoutInvoices"
                :key="item.id"
              >
                <td class="py-2">{{ item.description }}</td>
                <td class="text-right py-2">
                  {{ toCurrencyValue(item.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import systemSettingServer from "../../../server/systemSetting";
import statementServer from "../../../server/statement";

export default {
  name: "BillingDocumentStatementShowModal",
  props: ["showing", "statementId"],
  data() {
    return {
      error: "",
      systSettingsData: null,
      eocData: null,
      patientName: "",
      organizationState: "",
      statement: {
        refNumber: null,
        issuedDate: null,
        clientData: "",
        providerData: "",
        billTo: "",
        beginingBalance: null,
        endingBalance: null,
        lineItems: [],
        withoutInvoices: [],
        invoicesTotal: null,
        paymentsTotal: null,
        singleUrl: null,
        singleBlobUrl: null
      }
    };
  },
  async mounted() {
    this.systSettingsData = await systemSettingServer.get();
    this.fetchStates();
    this.organizationState = this.systSettingsData?.organizationState
      ? this.states.find(
          s => s.value === this.systSettingsData.organizationState
        )?.data?.abbreviation
      : "";
    if (this.statementId != null) {
      this.fetchData();
    }
  },
  methods: {
    ...mapActions("client", ["fetchStates"]),
    async fetchData() {
      const data = {
        statementId: this.statementId
      };
      const statement = await statementServer.get(data);
      this.patientName = statement.episodeOfCare.patient.name;
      this.statement.clientData = statement.clientData;
      this.statement.issuedDate = this.formatDateOnly(statement.issueDate);
      this.statement.refNumber = statement.refNumber;
      this.statement.providerData = statement.providerData;
      this.statement.beginingBalance = statement.beginningBalance;
      this.statement.endingBalance = statement.endingBalance;
      this.statement.lineItems = statement.lineItems;
      this.statement.withoutInvoices = statement.encountersWithoutInvoice;
      this.statement.invoicesTotal = statement.invoicesTotal;
      this.statement.paymentsTotal = statement.paymentsTotal;
      this.statement.billTo = statement.billTo;
      this.statement.dateFrom = statement.dateFrom;
      this.statement.dateTo = statement.dateTo;

      this.singleUrl = statementServer.statementViewUrl(this.statementId);

      fetch(this.singleUrl.url, { headers: this.singleUrl.headers }).then(
        response => {
          response.blob().then(blob => {
            this.singleBlobUrl = URL.createObjectURL(blob);
          });
        }
      );
    },
    onCancel() {
      this.resetData();
      this.editorShown = false;
      this.$emit("closeModal");
    },
    resetData() {
      this.statement = null;
      this.statement = {
        refNumber: null,
        issuedDate: null,
        clientData: "",
        providerData: "",
        billTo: "",
        beginingBalance: null,
        endingBalance: null,
        lineItems: [],
        withoutInvoices: [],
        invoicesTotal: null,
        paymentsTotal: null
      };
      this.lineItemsBalance = 0;
    },
    calculateLineItemsBalance(value, payment, first = false) {
      if (first) this.lineItemsBalance = 0;
      this.lineItemsBalance = payment
        ? this.lineItemsBalance - value
        : this.lineItemsBalance + value;
      return this.toCurrencyValue(this.lineItemsBalance);
    },
    printSingle() {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.zIndex = -100;
      iframe.style.width = "2250px";
      iframe.src = this.singleBlobUrl;
      iframe.onload = function() {
        iframe.contentWindow.onafterprint = function() {
          document.body.removeChild(iframe);
        };

        setTimeout(() => {
          iframe.contentWindow.print();
        }, 500);
      };

      document.body.appendChild(iframe);
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    ...mapState("client", {
      states: state =>
        state.states.map(s => ({
          label: s.name,
          value: s.id,
          data: s
        }))
    })
  },
  watch: {
    showing: function(value, oldValue) {
      if (value != oldValue && value && this.episodeOfCareId) {
        this.fetchData();
      }
    }
  }
};
</script>
